"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {menuItems.map(item => <NavigationMenuItem key={item.id} data-index={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:!bg-transparent focus:!bg-transparent outline-none">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-background border-t border-border shadow-medium lg:hidden">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.map(item => <Link key={item.id} data-index={item.id} to={item.href || "#"} className="px-4 py-2 text-sm font-medium hover:bg-accent/50 rounded-md transition-all duration-[var(--duration-fast)] hover:!bg-accent/50" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background border-b border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "h-[60px] shadow-medium" : "h-[80px]"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between gap-4">
        {}
        <Link to="/#" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1484645/img/army_writing_tools_logo.png" alt="Army Writing Tools" className="h-7" />
        </Link>

        {}
        <MenuProvider menu_id="9" className="flex-1 flex items-center justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        {}
        <div className="flex items-center gap-2">
          {}
          <div className="hidden md:flex items-center">
            {searchOpen ? <div className="flex items-center gap-2 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                <Input type="search" placeholder="Search..." className="w-48 h-9" autoFocus onBlur={() => setSearchOpen(false)} />
              </div> : <Button variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent" onClick={() => setSearchOpen(true)} aria-label="Search">
                <Search className="size-5" />
              </Button>}
          </div>

          {}
          <Button asChild variant="outline" className="group relative overflow-hidden hover:!bg-transparent focus:!bg-transparent border-primary text-primary hover:text-primary-foreground transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]">
            <Link to="#available-writing-tools">
              <span className="absolute inset-0 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] origin-left" />
              <span className="relative z-10">Get Started</span>
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}